import React from 'react';

import d2dBlackIcon from '@assets/img/icons/d2d-black.png';
import { instructionStrings } from '@data/instruction';
import { useIsAdmin } from '@hooks/useIsAdmin';
import { InstructionType } from '@models/next/Instruction';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { Link, useLocation } from 'react-router-dom';

const strings = createStrings({
  ja: {
    customsInstruction: {
      title: `${instructionStrings.customsInstruction}`,
    },
    deliveryInstruction: {
      title: `${instructionStrings.deliveryInstruction}`,
    },
    to: (title: string) => `${title}へ`,
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  instructionEditDisplayContainer: {
    ...common.middleText,
    ...common.textGray700,
    height: '100vh',
    backgroundColor: theme.palette.grey[100],
    padding: 20,
  },
  instructionEditDisplayInner: {
    ...common.flexInline,
    ...common.itemsStart,
    justifyContent: 'center',
    maxWidth: 1200,
    margin: '0 auto',
    columnGap: 30,
    height: '100%',
  },
  instructionAsideContainer: {
    ...common.flexColumn,
    rowGap: '1rem',
    width: 220,
    flexShrink: 0,
    overflow: 'scroll',
    alignSelf: 'stretch',
  },
  instructionContentContainer: {
    alignSelf: 'stretch',
    flex: 1,
    backgroundColor: theme.palette.background.paper,
    padding: '0 8px',
    overflow: 'scroll',
  },
  instructionBarContainer: {
    backgroundColor: theme.palette.background.paper,
    padding: 10,
  },
  instructionBarTitle: {
    ...common.textBlack,
    ...common.normalText,
    ...common.fontWeight600,
    marginBottom: '0.625rem',
  },
  instructionTitleContainer: {
    ...common.flexInline,
    ...common.itemsCenter,
    ...common.largeText,
    ...common.fontWeight600Important,
    ...common.textBlack,
    padding: '30px 20px',
    columnGap: 12,
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
  instructionBodyContainer: {
    padding: '1rem 1rem 2rem 1rem',
  },
  instructionAlertContainer: {
    ...common.flexColumn,
    rowGap: '1rem',
    padding: '1.5rem 1rem 0rem 1rem',
  },
  instructionInputContainer: {
    ...common.flexColumn,
    flex: 1,
    rowGap: '0.5rem',
  },
  instructionHint: {
    ...common.textGray700,
    ...common.smallerText,
  },
  insuranceOption: {
    ...common.flexColumn,
    justifyContent: 'center',
    paddingLeft: 40,
    width: '100%',
    height: '100%',
  },
  clientActionsContainer: {
    ...common.flexColumn,
    padding: '13px 0',
    rowGap: 10,
  },
  fileLink: {
    ...common.middleText,
    wordBreak: 'break-all',
    padding: '0.25rem 0.5rem',
    display: 'block',
    overflow: 'hidden',
    backgroundColor: theme.palette.grey[100],
  },
  filesContainer: {
    ...common.flexColumn,
    rowGap: '0.5rem',
  },
  successMessage: {
    ...common.fontWeight700,
    color: theme.palette.primary.main,
  },
  overdueMessage: {
    ...common.fontWeight700,
    color: theme.palette.error.main,
  },
  alert: {
    margin: '1.6rem 1rem 0 1rem',
  },
  redirectLink: {
    ...common.flexColumn,
    ...common.middleText,
    marginTop: 7,
    rowGap: 5,
  },
  topGuidance: {
    ...common.textGray800,
    ...common.smallerText,
    ...common.fontWeight700,
    padding: '1.25rem 0 1rem 0',
  },
  split: {
    height: 1,
    backgroundColor: theme.palette.grey[300],
  },
}));

export interface InstructionContainerProps {
  d2dNo: string;
  variant: InstructionType;
  alert?: React.ReactNode;
  left: React.ReactNode;
  right: React.ReactNode;
}

export const InstructionContainer: React.FC<React.PropsWithChildren<InstructionContainerProps>> = ({
  d2dNo,
  variant,
  alert,
  left,
  right,
  children,
}) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const isAdmin = useIsAdmin();

  return (
    <div className={classes.instructionEditDisplayContainer}>
      <div className={classes.instructionEditDisplayInner}>
        <aside className={classes.instructionAsideContainer}>{left}</aside>
        <div className={classes.instructionContentContainer}>
          <div className={classes.instructionAlertContainer}>{alert}</div>

          <div className={classes.instructionTitleContainer}>
            <img src={d2dBlackIcon} alt="D2D Logo" width={100} height={23} />
            <span>
              <Link
                to={
                  isAdmin
                    ? `${url(
                        '/admin/booking/BookingDetails',
                      )}?d2dNo=${d2dNo}&tabActiveId=instruction&type=current`
                    : `${url('/utilities/Bookings/CurrentBookingDetails')}?d2dNo=${d2dNo}`
                }
              >
                {d2dNo}
              </Link>
              -{strings[variant].title}
            </span>
          </div>
          <div className={classes.instructionBodyContainer}>{children}</div>
        </div>
        <aside className={classes.instructionAsideContainer}>{right}</aside>
      </div>
    </div>
  );
};

export interface InstructionBarContainerProps {
  title?: React.ReactNode;
  noTitle?: boolean;
}

export const InstructionBarContainer: React.FC<
  React.PropsWithChildren<InstructionBarContainerProps>
> = ({ title, noTitle = false, children }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={classes.instructionBarContainer}>
      {!noTitle && <InstructionBarTitle>{title}</InstructionBarTitle>}
      {children}
    </div>
  );
};

export const InstructionBarTitle: React.FC<React.PropsWithChildren> = ({ children }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return <div className={classes.instructionBarTitle}>{children}</div>;
};

export const InstructionHint: React.FC<React.PropsWithChildren> = ({ children }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return <div className={classes.instructionHint}>{children}</div>;
};

export const InstructionInputContainer: React.FC<
  React.PropsWithChildren & {
    /**
     * Add a small padding on the top to align with the label on the left.
     */
    paddingTopForLabel?: true;
  }
> = ({ children, paddingTopForLabel }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div
      className={classes.instructionInputContainer}
      style={{ paddingTop: paddingTopForLabel ? 5 : 0 }}
    >
      {children}
    </div>
  );
};

export const InstructionSplit: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return <div className={classes.split} />;
};

export const InstructionRedirectLink: React.FC<{
  d2dNo: string;
  variant: InstructionType;
}> = ({ d2dNo, variant }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const location = useLocation();

  return (
    <div className={classes.redirectLink}>
      <Link
        className={classes.link}
        to={`${url(
          '/utilities/Bookings/DeliveryInstruction',
        )}?d2dNo=${d2dNo}&from=${encodeURIComponent(`${location.pathname}${location.search}`)}`}
      >
        <b>
          {strings.to(
            variant === 'customsInstruction'
              ? instructionStrings.deliveryInstruction
              : instructionStrings.customsInstruction,
          )}
        </b>
      </Link>
    </div>
  );
};
